/**
 * Parse Brazilian date format to JavaScript Date object
 * @param {string} dateStr - Date string in format "dd/mm/yyyy HH:mm:ss"
 * @returns {Date|null} - Date object or null if invalid
 */
export function parseBrazilianDate(dateStr) {
    if (!dateStr) return null;

    try {
        // Expected format: "dd/mm/yyyy HH:mm:ss"
        // Example: "22/10/2024 16:20:26"

        // If it's already an ISO date, return directly
        if (dateStr.includes('-') && dateStr.includes('T')) {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
                return date;
            }
        }

        // Parse Brazilian format
        const parts = dateStr.split(' ');
        if (!parts[0]) return null;

        const dateParts = parts[0].split('/');
        if (dateParts.length !== 3) return null;

        const day = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed in JS
        const year = parseInt(dateParts[2], 10);

        // Validate basic values
        if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return null;
        }

        let hour = 0, minute = 0, second = 0;

        // Parse time if it exists
        if (parts[1]) {
            const timeParts = parts[1].split(':');
            hour = parseInt(timeParts[0], 10) || 0;
            minute = parseInt(timeParts[1], 10) || 0;
            second = parseInt(timeParts[2], 10) || 0;
        }

        // Create date
        const date = new Date(year, month, day, hour, minute, second);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return null;
        }

        return date;
    } catch (error) {
        console.error('Error parsing Brazilian date:', dateStr, error);
        return null;
    }
}

/**
 * Format date to Brazilian format
 * @param {Date} date - Date object to format
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} - Formatted date string
 */
export function formatToBrazilianDate(date, includeTime = false) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    if (!includeTime) {
        return `${day}/${month}/${year}`;
    }

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

/**
 * Calculate age in days from a date
 * @param {Date|string} date - Date to calculate age from
 * @returns {number} - Age in days
 */
export function getAgeInDays(date) {
    const parsedDate = typeof date === 'string' ? parseBrazilianDate(date) : date;
    if (!parsedDate) return 0;

    const now = new Date();
    const diffTime = Math.abs(now - parsedDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}